import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { LuFileText } from "react-icons/lu";
import { RiBookOpenLine, RiCalendarLine, RiCheckLine, RiCheckboxLine, RiCloseLine, RiDoorOpenLine, RiEyeLine, RiEyeOffLine, RiFileAddLine, RiFileCopyLine, RiHospitalLine, RiLogoutBoxRLine, RiMessage3Line, RiShoppingBagLine, RiSparklingLine, RiStickyNoteLine, RiTabletLine, RiUserAddLine, RiUserFollowLine } from "react-icons/ri";
import { getWindowBaseURL } from "@/lib/get-base-url";
import { PERMISSIONS, hasAnyPermission } from "@/lib/permissions";
import { SHOP_SECTIONS, getShopUrl } from "@/lib/shop-links";
import { ProviderSummary } from "@/types/provider";
import type { Module, Tenant } from "@/types/tenant";
import { useUpdateShowIdsMutation, useUpdateShowMessagesBoxMutation, useUpdateShowPatientIdsMutation, useUpdateShowWeekendsMutation } from "@/query/settings/preferences";
import { authService } from "@/services/auth-service";
import { CommandPaletteModal, useCommandPaletteModalsStore } from "@/stores/command-palette-modals-store";
import { usePatientStatusDrawerStore } from "@/stores/patient-status-drawer-store";
import { useTeamChatDrawerStore } from "@/stores/team-chat-store";
import { useThemeStore } from "@/stores/theme-store";

/**
 * Command Action Definition
 *
 * Actions are executable commands that appear in the command palette (Cmd+K).
 * Unlike navigation items (pages), actions perform operations directly.
 *
 * @example
 * // Define a new action
 * {
 *   id: 'example-action',
 *   name: 'Example Action',
 *   description: 'Perform an example action',
 *   keywords: ['example', 'action'],
 *   icon: <RiRefreshLine size={16} />,
 *   category: 'action',
 *   onSelect: async () => {
 *     await performAction();
 *   },
 * }
 */
export interface CommandAction {
  /** Unique identifier for the action */
  id: string;
  /** Display name shown in the command palette */
  name: string;
  /** Optional description shown below the name */
  description?: string;
  /** Keywords for search matching */
  keywords?: string[];
  /** Icon displayed next to the action */
  icon?: React.ReactNode;
  /** Keyboard shortcut hint (display only) */
  shortcut?: string;
  /** Category for grouping actions */
  category: "action" | "navigation" | "settings";
  /** Whether this is a destructive action (shown in red) */
  isDestructive?: boolean;
  /** Handler called when the action is selected */
  onSelect: () => void | Promise<void>;
  /**
   * Optional function to determine if action should be shown.
   * Return false to hide the action from the command palette.
   * Receives the current user to avoid stale closure issues.
   */
  isVisible?: (user: ProviderSummary | null | undefined) => boolean;
  /**
   * Permissions required to see this action.
   * If specified, user must have at least one of these permissions.
   */
  permissions?: string[];
  /**
   * If set, the action is only shown when this tenant module is enabled
   * (e.g. Growth uses MARKETING).
   */
  requiredModule?: Module;
  /** Also offered on an enterprise MASTER. */
  onEnterprise?: boolean;
}

/**
 * Hook to get mutation instances (called once per component mount)
 * The mutations object is memoized to ensure stable reference
 */
const useCommandActionMutations = () => {
  const $ = _c(5);
  const updateShowIds = useUpdateShowIdsMutation();
  const updateShowPatientIds = useUpdateShowPatientIdsMutation();
  const updateShowWeekends = useUpdateShowWeekendsMutation();
  const updateShowMessagesBox = useUpdateShowMessagesBoxMutation();
  let t0;
  if ($[0] !== updateShowIds || $[1] !== updateShowMessagesBox || $[2] !== updateShowPatientIds || $[3] !== updateShowWeekends) {
    t0 = {
      updateShowIds,
      updateShowPatientIds,
      updateShowWeekends,
      updateShowMessagesBox
    };
    $[0] = updateShowIds;
    $[1] = updateShowMessagesBox;
    $[2] = updateShowPatientIds;
    $[3] = updateShowWeekends;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
};

/**
 * Creates the command actions array given mutation instances
 */
const createCommandActions = (mutations: ReturnType<typeof useCommandActionMutations>): CommandAction[] => {
  const {
    updateShowIds,
    updateShowPatientIds,
    updateShowWeekends,
    updateShowMessagesBox
  } = mutations;
  const whisperKeywords = ["whisper", "team chat", "message", "messages", "internal chat", "chat team", "dm", "direct message"];
  const patientStatusKeywords = ["patient status", "waiting room", "check-in", "waiting"];
  return [{
    id: "open-whisper",
    name: "Open Whisper",
    description: "Open team chat",
    keywords: whisperKeywords,
    icon: <RiMessage3Line size={16} />,
    category: "action",
    requiredModule: "WHISPER",
    isVisible: () => !useTeamChatDrawerStore.getState().isOpen,
    onSelect: () => {
      useTeamChatDrawerStore.getState().openDrawer();
    }
  }, {
    id: "close-whisper",
    name: "Close Whisper",
    description: "Close team chat",
    keywords: whisperKeywords,
    icon: <RiMessage3Line size={16} />,
    category: "action",
    requiredModule: "WHISPER",
    isVisible: () => useTeamChatDrawerStore.getState().isOpen,
    onSelect: () => {
      useTeamChatDrawerStore.getState().closeDrawer();
    }
  }, {
    id: "open-patient-status",
    name: "Open Patient Status",
    description: "View patient check-in and waiting status",
    keywords: patientStatusKeywords,
    icon: <RiDoorOpenLine size={16} />,
    category: "action",
    requiredModule: "CALENDAR_PATIENT_STATUS",
    isVisible: () => !usePatientStatusDrawerStore.getState().isOpen,
    onSelect: () => {
      usePatientStatusDrawerStore.getState().openDrawer();
    }
  }, {
    id: "close-patient-status",
    name: "Close Patient Status",
    description: "Close patient check-in and waiting status",
    keywords: patientStatusKeywords,
    icon: <RiDoorOpenLine size={16} />,
    category: "action",
    requiredModule: "CALENDAR_PATIENT_STATUS",
    isVisible: () => usePatientStatusDrawerStore.getState().isOpen,
    onSelect: () => {
      usePatientStatusDrawerStore.getState().closeDrawer();
    }
  }, {
    id: "create-patient",
    name: "Create Patient",
    description: "Create a new patient record",
    keywords: ["create patient", "new patient", "add patient", "register patient"],
    icon: <RiUserAddLine size={16} />,
    category: "action",
    permissions: [PERMISSIONS.PATIENTS_CREATE, PERMISSIONS.PATIENTS_PROFILES_WRITE],
    onSelect: () => {
      useCommandPaletteModalsStore.getState().openModal(CommandPaletteModal.CREATE_PATIENT);
    }
  }, {
    id: "create-opportunity",
    name: "Create Lead",
    description: "Add a new prospective patient to a pipeline",
    keywords: ["lead", "create lead", "new lead", "add lead", "create opportunity", "new opportunity", "add opportunity", "opportunity", "prospect", "pipeline", "growth"],
    icon: <RiUserFollowLine size={16} />,
    category: "action",
    permissions: [PERMISSIONS.GROWTH_WRITE],
    requiredModule: "MARKETING",
    onSelect: () => {
      useCommandPaletteModalsStore.getState().openModal(CommandPaletteModal.CREATE_OPPORTUNITY);
    }
  }, {
    id: "send-forms",
    name: "Send Forms",
    description: "Send forms to a patient",
    keywords: ["send forms", "forms", "intake", "patient forms", "send intake"],
    icon: <LuFileText size={16} />,
    category: "action",
    permissions: [PERMISSIONS.MESSAGING_CHATS_WRITE],
    onSelect: () => {
      useCommandPaletteModalsStore.getState().openModal(CommandPaletteModal.SEND_FORMS);
    }
  }, {
    id: "create-memo",
    name: "Create Memo",
    description: "Create a memo for a patient",
    keywords: ["create memo", "new memo", "add memo", "memo", "patient memo"],
    icon: <RiStickyNoteLine size={16} />,
    category: "action",
    permissions: [PERMISSIONS.PATIENTS_NOTES_WRITE],
    onSelect: () => {
      useCommandPaletteModalsStore.getState().openModal(CommandPaletteModal.CREATE_MEMO);
    }
  }, {
    id: "create-note",
    name: "Create Note",
    description: "Create a new AI or manual clinical note",
    keywords: ["scribe", "note", "ai scribe", "clinical note", "create note"],
    icon: <RiFileAddLine size={16} />,
    category: "action",
    permissions: [PERMISSIONS.PATIENTS_NOTES_WRITE],
    onSelect: () => {
      useCommandPaletteModalsStore.getState().openModal(CommandPaletteModal.CREATE_NOTE);
    }
  }, {
    id: "create-task",
    name: "Create Task",
    description: "Create a new task",
    keywords: ["create task", "new task", "add task", "todo", "assign task"],
    icon: <RiCheckboxLine size={16} />,
    category: "action",
    permissions: [PERMISSIONS.TASKS_WRITE],
    onSelect: () => {
      useCommandPaletteModalsStore.getState().openModal(CommandPaletteModal.CREATE_TASK);
    }
  }, {
    id: "copy-page-url",
    onEnterprise: true,
    name: "Copy Page URL",
    description: "Copy the current page URL to clipboard",
    keywords: ["copy", "url", "link", "share", "clipboard"],
    icon: <RiFileCopyLine size={16} />,
    category: "action",
    onSelect: async () => {
      await navigator.clipboard.writeText(window.location.href);
      // Toast will be shown by the navigation-search component
    }
  }, {
    id: "copy-scheduling-link",
    name: "Copy Scheduling Link",
    description: "Copy the self-scheduling link to clipboard",
    keywords: ["copy", "scheduling", "self-schedule", "appointment", "book", "link", "share"],
    icon: <RiCalendarLine size={16} />,
    category: "action",
    onSelect: async () => {
      const tenant = window.location.pathname.split("/")[1];
      const schedulingLink = `${getWindowBaseURL(tenant)}/self-schedule`;
      await navigator.clipboard.writeText(schedulingLink);
      // Toast will be shown by the navigation-search component
    }
  }, {
    id: "copy-shop-link",
    name: "Copy Shop Link",
    description: "Copy the online shop link to clipboard",
    keywords: ["copy", "shop", "store", "online", "purchase", "buy", "link", "share"],
    icon: <RiShoppingBagLine size={16} />,
    category: "action",
    requiredModule: "SHOP",
    onSelect: async () => {
      const tenant = window.location.pathname.split("/")[1];
      const shopLink = getShopUrl(getWindowBaseURL(tenant));
      await navigator.clipboard.writeText(shopLink);
    }
  }, {
    id: "copy-gift-card-link",
    name: "Copy Gift Card Link",
    description: "Copy the gift card shop link to clipboard",
    keywords: ["copy", "gift card", "shop", "store", "online", "purchase", "buy", "link", "share"],
    icon: <RiShoppingBagLine size={16} />,
    category: "action",
    requiredModule: "SHOP",
    onSelect: async () => {
      const tenant = window.location.pathname.split("/")[1];
      const giftCardLink = getShopUrl(getWindowBaseURL(tenant), SHOP_SECTIONS.giftCards);
      await navigator.clipboard.writeText(giftCardLink);
    }
  }, {
    id: "copy-package-link",
    name: "Copy Package Link",
    description: "Copy the package shop link to clipboard",
    keywords: ["copy", "package", "packages", "shop", "store", "online", "purchase", "buy", "link", "share"],
    icon: <RiShoppingBagLine size={16} />,
    category: "action",
    requiredModule: "SHOP",
    onSelect: async () => {
      const tenant = window.location.pathname.split("/")[1];
      const packageLink = getShopUrl(getWindowBaseURL(tenant), SHOP_SECTIONS.packages);
      await navigator.clipboard.writeText(packageLink);
    }
  }, {
    id: "copy-check-in-link",
    name: "Copy Check-In Link",
    description: "Copy the patient self check-in link to clipboard",
    keywords: ["copy", "check in", "check-in", "checkin", "kiosk", "ipad", "front desk", "arrival", "link", "share"],
    icon: <RiUserFollowLine size={16} />,
    category: "action",
    onSelect: async () => {
      const tenant = window.location.pathname.split("/")[1];
      const checkInLink = `${getWindowBaseURL(tenant)}/patient-check-in`;
      await navigator.clipboard.writeText(checkInLink);
    }
  }, {
    id: "copy-patient-portal-link",
    name: "Copy Patient Portal Link",
    description: "Copy the patient portal login link to clipboard",
    keywords: ["copy", "patient portal", "portal", "login", "patient login", "my health", "link", "share"],
    icon: <RiHospitalLine size={16} />,
    category: "action",
    onSelect: async () => {
      const tenant = window.location.pathname.split("/")[1];
      const portalLink = `${getWindowBaseURL(tenant)}/patient-portal/login`;
      await navigator.clipboard.writeText(portalLink);
    }
  }, {
    id: "copy-tip-screen-link",
    name: "Copy Tip Screen Link",
    description: "Copy the customer-facing tip screen link to clipboard",
    keywords: ["copy", "tip", "tips", "tipping", "gratuity", "tip screen", "tip display", "customer display", "customer facing", "ipad", "tablet", "link", "share"],
    icon: <RiTabletLine size={16} />,
    category: "action",
    requiredModule: "PAYMENTS",
    onSelect: async () => {
      const tenant = window.location.pathname.split("/")[1];
      const tipScreenLink = `${getWindowBaseURL(tenant)}/tip-screen`;
      await navigator.clipboard.writeText(tipScreenLink);
    }
  }, {
    id: "open-skin-analysis",
    name: "Open Skin Analysis",
    description: "Open the skin analysis page to share with patients",
    keywords: ["skin", "analysis", "skin age", "share", "facial", "selfie"],
    icon: <RiSparklingLine size={16} />,
    category: "action",
    onSelect: () => {
      const tenant = window.location.pathname.split("/")[1];
      const link = `${getWindowBaseURL(tenant)}/skin-analysis`;
      window.open(link, "_blank");
    }
  }, {
    id: "view-documentation",
    onEnterprise: true,
    name: "View Documentation",
    description: "Open the help documentation",
    keywords: ["docs", "documentation", "help", "guide", "manual", "support"],
    icon: <RiBookOpenLine size={16} />,
    category: "action",
    onSelect: () => {
      window.open("https://docs.decodahealth.com", "_blank");
    }
  },
  // Preference Toggles
  {
    id: "toggle-show-ids-on",
    onEnterprise: true,
    name: "Show IDs",
    description: "Enable showing IDs on every item in the UI",
    keywords: ["show ids", "enable ids", "display ids", "ids on", "settings"],
    icon: <RiEyeLine size={16} />,
    category: "settings",
    isVisible: user => {
      return user !== null && user !== undefined && !user?.showIds;
    },
    onSelect: async () => {
      updateShowIds.mutate(true);
    }
  }, {
    id: "toggle-show-ids-off",
    onEnterprise: true,
    name: "Hide IDs",
    description: "Disable showing IDs on items in the UI",
    keywords: ["hide ids", "disable ids", "ids off", "settings"],
    icon: <RiEyeOffLine size={16} />,
    category: "settings",
    isVisible: user => {
      return user?.showIds === true;
    },
    onSelect: async () => {
      updateShowIds.mutate(false);
    }
  }, {
    id: "toggle-show-patient-ids-on",
    onEnterprise: true,
    name: "Show Patient IDs",
    description: "Enable showing patient IDs (MRN or Decoda ID)",
    keywords: ["show patient ids", "show mrn", "enable patient ids", "patient ids on", "settings"],
    icon: <RiEyeLine size={16} />,
    category: "settings",
    isVisible: user => {
      return user !== null && user !== undefined && !user?.showPatientIds;
    },
    onSelect: async () => {
      updateShowPatientIds.mutate(true);
    }
  }, {
    id: "toggle-show-patient-ids-off",
    onEnterprise: true,
    name: "Hide Patient IDs",
    description: "Disable showing patient IDs (MRN or Decoda ID)",
    keywords: ["hide patient ids", "hide mrn", "disable patient ids", "patient ids off", "settings"],
    icon: <RiEyeOffLine size={16} />,
    category: "settings",
    isVisible: user => {
      return user?.showPatientIds === true;
    },
    onSelect: async () => {
      updateShowPatientIds.mutate(false);
    }
  }, {
    id: "toggle-show-weekends-on",
    name: "Show Weekends",
    description: "Show weekends in the calendar view",
    keywords: ["show weekends", "enable weekends", "weekends on", "calendar", "settings"],
    icon: <RiCheckLine size={16} />,
    category: "settings",
    isVisible: user => {
      return user !== null && user !== undefined && !user?.showWeekendsOnCalendar;
    },
    onSelect: async () => {
      updateShowWeekends.mutate(true);
    }
  }, {
    id: "toggle-show-weekends-off",
    name: "Hide Weekends",
    description: "Hide weekends in the calendar view",
    keywords: ["hide weekends", "disable weekends", "weekends off", "calendar", "settings"],
    icon: <RiCloseLine size={16} />,
    category: "settings",
    isVisible: user => {
      return user?.showWeekendsOnCalendar === true;
    },
    onSelect: async () => {
      updateShowWeekends.mutate(false);
    }
  }, {
    id: "toggle-show-messages-box-on",
    name: "Show Messages Box",
    description: "Display the messages box in the bottom right corner",
    keywords: ["show messages", "messages box", "chat box", "communications", "settings"],
    icon: <RiCheckLine size={16} />,
    category: "settings",
    isVisible: user => {
      return user !== null && user !== undefined && !user?.showMessagesBox;
    },
    onSelect: async () => {
      updateShowMessagesBox.mutate(true);
    }
  }, {
    id: "toggle-show-messages-box-off",
    name: "Hide Messages Box",
    description: "Hide the messages box from the bottom right corner",
    keywords: ["hide messages", "messages box", "chat box", "communications", "settings"],
    icon: <RiCloseLine size={16} />,
    category: "settings",
    isVisible: user => {
      return user?.showMessagesBox === true;
    },
    onSelect: async () => {
      updateShowMessagesBox.mutate(false);
    }
  }, {
    id: "toggle-transparency-off",
    onEnterprise: true,
    name: "Turn Off Transparency",
    description: "Use solid backgrounds instead of translucent blur effects",
    keywords: ["transparency", "blur", "backdrop", "solid", "opaque", "accessibility", "settings"],
    icon: <RiEyeOffLine size={16} />,
    category: "settings",
    isVisible: () => useThemeStore.getState().transparency,
    onSelect: () => {
      useThemeStore.getState().setTransparency(false);
    }
  }, {
    id: "toggle-transparency-on",
    onEnterprise: true,
    name: "Turn On Transparency",
    description: "Use translucent blur effects for panels and overlays",
    keywords: ["transparency", "blur", "backdrop", "translucent", "glass", "settings"],
    icon: <RiEyeLine size={16} />,
    category: "settings",
    isVisible: () => !useThemeStore.getState().transparency,
    onSelect: () => {
      useThemeStore.getState().setTransparency(true);
    }
  }, {
    id: "patient-portal",
    name: "Patient Portal",
    description: "Open the patient portal",
    keywords: ["patient portal", "patient", "portal", "my health", "patient view"],
    icon: <RiHospitalLine size={16} />,
    category: "navigation",
    onSelect: () => {
      const tenant = window.location.pathname.split("/")[1];
      window.location.href = `/${tenant}/patient-portal/login`;
    }
  }, {
    id: "sign-out",
    onEnterprise: true,
    name: "Sign Out",
    description: "Sign out of your account",
    keywords: ["sign out", "logout", "log out", "exit", "leave"],
    icon: <RiLogoutBoxRLine size={16} />,
    category: "action",
    isDestructive: true,
    onSelect: async () => {
      await authService.logout();
      window.location.href = "/login";
    }
  }];
};

/**
 * Hook to get command actions and settings separately
 * Mutations are created once per component mount and reused.
 * The actions arrays are memoized to prevent unnecessary recreations.
 */
export const useCommandActions = () => {
  const $ = _c(6);
  const mutations = useCommandActionMutations();
  let actions;
  let t0;
  if ($[0] !== mutations) {
    const allActions = createCommandActions(mutations);
    actions = allActions.filter(_temp);
    t0 = allActions.filter(_temp2);
    $[0] = mutations;
    $[1] = actions;
    $[2] = t0;
  } else {
    actions = $[1];
    t0 = $[2];
  }
  const settings = t0;
  let t1;
  if ($[3] !== actions || $[4] !== settings) {
    t1 = {
      actions,
      settings
    };
    $[3] = actions;
    $[4] = settings;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};

/**
 * Filter actions based on search term, visibility, and user permissions
 */
export const filterCommandActions = (actions: CommandAction[], searchTerm: string, user: ProviderSummary | null | undefined, minSearchLength: number = 2, tenant?: Pick<Tenant, "enabledModules" | "kind"> | null): CommandAction[] => {
  if (searchTerm.length < minSearchLength) {
    return [];
  }
  const searchLower = searchTerm.toLowerCase();
  return actions.filter(action => {
    // Check visibility function - pass user to avoid stale closure issues
    if (action.isVisible && !action.isVisible(user)) {
      return false;
    }
    if (tenant?.kind === "MASTER" && !action.onEnterprise) {
      return false;
    }
    if (action.requiredModule && !tenant?.enabledModules?.includes(action.requiredModule)) {
      return false;
    }

    // Check permissions - if permissions specified, user must have at least one
    if (action.permissions && !hasAnyPermission(user, action.permissions)) {
      return false;
    }

    // Check search match
    return action.name.toLowerCase().startsWith(searchLower) || action.description?.toLowerCase().startsWith(searchLower) || action.keywords?.some(k => k.toLowerCase().startsWith(searchLower));
  }).sort((a, b) => {
    // Prioritize non-settings actions over settings actions
    const aIsSettings = a.category === "settings";
    const bIsSettings = b.category === "settings";
    if (aIsSettings !== bIsSettings) {
      return aIsSettings ? 1 : -1;
    }

    // Within the same category, prioritize name matches
    const aNameMatch = a.name.toLowerCase().startsWith(searchLower);
    const bNameMatch = b.name.toLowerCase().startsWith(searchLower);
    return aNameMatch === bNameMatch ? 0 : aNameMatch ? -1 : 1;
  });
};
function _temp(action) {
  return action.category !== "settings";
}
function _temp2(action_0) {
  return action_0.category === "settings";
}